/**
 * Utility for scrolling to and briefly highlighting a transcript turn element.
 *
 * @module lib/utils/transcript/highlight-turn
 */

/** CSS classes applied as the highlight ring. Flat: a volt ring, no glow. */
const HIGHLIGHT_CLASSES = ['ring-2', 'ring-volt'] as const;

/**
 * Scrolls an element into view and applies a temporary highlight ring.
 *
 * After `delayMs` milliseconds (default 0), the element is scrolled into view
 * and highlight classes are added. The classes are removed automatically after
 * 3000ms. The returned cleanup function cancels both timers, preventing any
 * pending scroll, class addition, or removal.
 *
 * @param el - The DOM element to scroll to and highlight.
 * @param delayMs - Optional delay before scrolling and highlighting, in milliseconds.
 * @returns A cleanup function that cancels all pending timers.
 */
export function highlightTurn(el: Element, delayMs = 0): () => void {
  let removalTimer: ReturnType<typeof setTimeout> | undefined;

  const delayTimer = setTimeout(() => {
    (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add(...HIGHLIGHT_CLASSES);
    removalTimer = setTimeout(() => {
      el.classList.remove(...HIGHLIGHT_CLASSES);
    }, 3000);
  }, delayMs);

  return () => {
    clearTimeout(delayTimer);
    clearTimeout(removalTimer);
  };
}
